import React, { useState } from 'react';
import { Box } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';

const MONO = "'IBM Plex Mono', monospace";

// One option row for a text-label picker (here and the Name field's picker in
// DrawingSidePane.js): the label text, then what it's already linked to, with
// "nearby" when it sits close to the item being edited. `entry` comes from
// utils/textLabelOptions.js's rankTextLabelOptions. Same-text labels are common
// (a tag beside its pump and again where a line heads to it), so this is what
// tells them apart.
export const renderTextLabelOption = (props, entry) => (
  <Box component="li" {...props} key={entry.label._id}
    sx={{ display: 'flex !important', flexDirection: 'column', alignItems: 'flex-start !important', gap: '1px', py: '6px !important' }}>
    <Box sx={{ fontFamily: MONO, fontSize: '12.5px', color: '#eaf0fb' }}>{entry.label.name || '(unnamed label)'}</Box>
    <Box sx={{ fontSize: '11px', color: entry.links === 'not linked' ? '#7f93b8' : '#a9bcdc', display: 'flex', gap: '6px', alignItems: 'center' }}>
      {entry.nearby && <Box component="span" sx={{ color: '#2dd4bf', fontFamily: MONO, fontSize: '10px', letterSpacing: '.04em' }}>NEARBY</Box>}
      <span>{entry.links}</span>
    </Box>
  </Box>
);

// Picker + list for `describes` connections on the free-text Description
// field (DrawingSidePane.js's Element/Connection edit forms). Sibling to,
// but deliberately not sharing code with, the existing Name-field "Link text
// label" picker (renderNamePicker there) — that one re-points a single slot
// per target (a name is singular); this one only ever *adds* a new edge,
// since a target can have several describes-labels (that's the actual
// point — the same label text, e.g. a repeated valve-type/spec code, can
// describe many different elements). Purely props-driven: every API call
// happens in the parent (DrawingSidePane.js), this only renders and calls
// back, matching CanvasStackPicker/DrawingMinimap's shape from earlier work.
const DescribesPicker = ({
  describesConnections, // connections already relationshipType:'describes' targeting the current item
  labelOptions,         // ranked candidate text labels (utils/textLabelOptions.js rankTextLabelOptions)
  onLink,                // (label) => void — already bound to the target by the caller
  onUnlink,              // (connection) => void
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <Box sx={{ mt: '-8px', mb: '14px' }}>
      {describesConnections.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', mb: '8px' }}>
          {describesConnections.map(conn => {
            const labelText = conn.name || (typeof conn.source === 'object' ? conn.source?.name : null) || '(unnamed label)';
            return (
              <Box key={conn._id} sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '9px', py: '6px', borderRadius: '7px', border: '1px solid rgba(120,150,200,.18)', background: 'rgba(120,150,200,.03)' }}>
                <LabelOutlinedIcon sx={{ fontSize: 13, color: '#7f93b8', flexShrink: 0 }} />
                <Box sx={{ fontFamily: MONO, fontSize: '12px', color: '#5fe6d6', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {labelText}
                </Box>
                <Box component="button" onClick={() => onUnlink(conn)} title="Unlink this description label"
                  sx={{ background: 'transparent', border: 'none', color: '#7f93b8', cursor: 'pointer', fontSize: '13px', p: '2px 4px', borderRadius: '4px', lineHeight: 1, flexShrink: 0, '&:hover': { color: '#f4716a' } }}>
                  ×
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {!pickerOpen ? (
        <Box component="button" onClick={() => setPickerOpen(true)} title="Link an existing text label as a description"
          sx={{ background: 'transparent', border: '1px solid rgba(120,150,200,.24)', color: '#7f93b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', px: '8px', py: '4px', borderRadius: '7px', fontSize: '11.5px', fontFamily: "'Lexend', sans-serif", '&:hover': { color: '#2dd4bf', borderColor: 'rgba(45,212,191,.5)' } }}>
          <LabelOutlinedIcon sx={{ fontSize: 14 }} /> Link description label
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Autocomplete
            openOnFocus
            autoHighlight
            size="small"
            fullWidth
            options={labelOptions}
            getOptionLabel={(entry) => entry.label.name || '(unnamed label)'}
            isOptionEqualToValue={(a, b) => a.label._id === b.label._id}
            onChange={(_, picked) => { if (picked) { setPickerOpen(false); onLink(picked.label); } }}
            renderOption={renderTextLabelOption}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                placeholder="Search text labels…"
                sx={{
                  '& .MuiInputBase-root': { background: '#0c1a32', fontSize: '13px', fontFamily: MONO, color: '#eaf0fb', borderRadius: '8px' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(120,150,200,.26)' },
                  '& .MuiInputBase-input::placeholder': { color: '#5f7398', opacity: 1 },
                }}
              />
            )}
            slotProps={{
              paper: { sx: { background: '#0d1a33', border: '1px solid rgba(120,150,200,.24)', borderRadius: '10px', mt: '4px' } },
            }}
          />
          <Box component="button" onClick={() => setPickerOpen(false)}
            sx={{ background: 'transparent', border: 'none', color: '#7f93b8', cursor: 'pointer', fontSize: '14px', p: '2px 5px', borderRadius: '4px', lineHeight: 1, flexShrink: 0, '&:hover': { color: '#eaf0fb' } }}>
            ✕
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DescribesPicker;
